import Pagination from 'components/common/Pagination';
import RelativeTime from 'components/common/RelativeTime';
import axios from 'helpers/axios';
import config from 'helpers/config';
import dateFormats from 'helpers/dateFormats';
import SearchResults from 'models/SearchResultsModel';
import TableColumn, { TableColumOrderStatus } from 'models/TableColumnModel';
import UserLogin from 'models/UserLoginModel';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FaSort, FaSortDown, FaSortUp } from 'react-icons/fa';

interface Props {
    userId: string;
    onLoginsCountChange?: (count: number) => void;
}

const UserLoginsList = ({ userId, onLoginsCountChange }: Props) => {
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        orderBy: 'createdAt',
        orderType: TableColumOrderStatus.Ascending,

        totalCount: 0,
    });
    const [userLogins, setUserLogins] = useState<UserLogin[]>([]);

    useEffect(() => {
        getUserLogins();
    }, [pagination.page, pagination.orderBy, pagination.orderType]);

    const getUserLogins = () => {
        axios
            .get<SearchResults<UserLogin>>(`${config.apiUrl}/auth/userlogins/${userId}`, {
                params: {
                    ...pagination,
                },
            })
            .then((res) => {
                setUserLogins(res.data.rows);
                setPagination({ ...pagination, totalCount: res.data.count });
                onLoginsCountChange?.(res.data.count);
            });
    };

    const handleSort = (column: TableColumn) => {
        if (column.orderStatus === TableColumOrderStatus.None) {
            column.orderStatus = TableColumOrderStatus.Ascending;
        } else if (column.orderStatus === TableColumOrderStatus.Ascending) {
            column.orderStatus = TableColumOrderStatus.Descending;
        } else {
            column.orderStatus = TableColumOrderStatus.None;
        }

        const newPagination = {
            ...pagination,
            orderBy: column.name,
            orderType: column.orderStatus,
        };

        setPagination(newPagination);
    };

    return (
        <>
            <div className="overflow-x-scroll">
                <table className="items-center bg-transparent border w-full mb-0.5">
                    <thead className="border">
                        <tr className="bg-blue-600 text-white border-none font-semibold text-left text-xs uppercase align-middle">
                            {userLoginsListColumns.map(
                                (column, index) =>
                                    column.active && (
                                        <th
                                            key={index}
                                            className={`pl-6 py-3 ${index === 0 && 'rounded-tl-md'} ${
                                                index === userLoginsListColumns.length - 1 && 'rounded-tr-md'
                                            }`}
                                        >
                                            <span
                                                className={column.sortable ? `clickable` : ''}
                                                onClick={() => column.sortable && handleSort(column)}
                                            >
                                                <span>{column.text}</span>
                                                {!!column.sortable &&
                                                    (column?.orderStatus === TableColumOrderStatus.None ? (
                                                        <FaSort className="inline ml-1" />
                                                    ) : column?.orderStatus === TableColumOrderStatus.Ascending ? (
                                                        <FaSortDown className="inline ml-1" />
                                                    ) : (
                                                        <FaSortUp className="inline ml-1" />
                                                    ))}
                                            </span>
                                        </th>
                                    ),
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {userLogins?.map((login, index) => (
                            <tr
                                className={`border group font-medium hover:bg-blue-200 clickable ${
                                    index % 2 === 0 ? 'bg-gray-100' : 'bg-blue-100'
                                }`}
                                key={login.id}
                            >
                                <td className="px-6 align-middle whitespace-nowrap py-1.5 text-blue-400 font-semibold">
                                    <span className="float-left">
                                        <RelativeTime dateTime={login.createdAt} floatRight={false} />
                                    </span>
                                    <br />
                                    <span>
                                        <span className="text-sm">
                                            {moment(login.createdAt).format(dateFormats.USA_DATETIME)}
                                        </span>
                                    </span>
                                </td>
                                <td className="px-6 align-middle border-l-0 border-r-0 text-base whitespace-nowrap py-2 font-semibold">
                                    <span>{login.ipAddress}</span>
                                </td>
                                <td className="px-6 align-middle border-l-0 border-r-0 text-base whitespace-nowrap py-2">
                                    <span>{login.userAgent} </span>
                                </td>
                                <td className="px-6 align-middle whitespace-nowrap  non-clickable">
                                    <span className="font-semibold">{login.deviceType}</span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {userLogins?.length! < 1 && (
                    <div className="border py-4 px-4 wb-white text-gray-600 text-lg font-semibold text-center">
                        This user has not logged in yet.
                    </div>
                )}
            </div>

            <Pagination
                currentPage={pagination.page}
                totalItems={pagination.totalCount!}
                pageSize={pagination.limit}
                pageChanged={(newPage: number) => {
                    setPagination({ ...pagination, page: newPage });
                }}
            />
        </>
    );
};

export default UserLoginsList;

const userLoginsListColumns: TableColumn[] = [
    {
        name: 'createdAt',
        active: true,
        text: 'Date',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'ipAddress',
        active: true,
        text: 'IP Address',
        sortable: false,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'userAgent',
        active: true,
        text: 'User Agent',
        sortable: false,
        orderStatus: TableColumOrderStatus.None,
    },
    {
        name: 'deviceType',
        active: true,
        text: 'Device Type',
        sortable: true,
        orderStatus: TableColumOrderStatus.None,
    },
];
